/**
 * The slug → venue id hop every venue-scoped page starts with, plus whatever
 * that page loads once it has the id.
 *
 * The URL carries a slug and every API route takes a numeric venue id, so each
 * public page opens with the same two-step: by-slug, then the real read. It was
 * written out inline in PublicStandings, PublicTournaments, HallOfFame and
 * VenueLanding before Stage 4 added four more pages that needed it — at which
 * point the cancellation flag, the venue-not-found branch and the error shape
 * were being copied rather than shared.
 *
 * The existing pages are deliberately NOT converted here. They work, and
 * rewriting four working pages to prove a hook is a change with no user-visible
 * result; they can adopt it when they are next touched for another reason.
 *
 * `load` is called with the venue and must return the same { data, error } shape
 * apiGet does, so a page that needs two reads can Promise.all them and hand back
 * one object. It is NOT in the dependency array — an inline arrow is a new
 * function on every render, so depending on it would refetch forever. Pass
 * anything the loader reads in `deps` instead, which is why deps exists at all.
 *
 * Returns { status: 'loading' | 'error' | 'ready', venue, data, error, reload }.
 * `venue` is present on an error caused by the second read, and absent when the
 * slug itself did not resolve — which is what lets a page tell "no such venue"
 * from "this page is unavailable".
 */

import { useCallback, useEffect, useState } from 'react';
import { apiGet } from './api.js';

export function useVenuePage(venueSlug, load, deps = []) {
  const [state, setState] = useState({ status: 'loading' });
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });

    (async () => {
      const { data, error } = await apiGet(`/api/venues/by-slug/${venueSlug}`);
      if (cancelled) return;

      if (error || !data?.venue) {
        setState({ status: 'error', error: error ?? 'Venue not found' });
        return;
      }

      const venue = data.venue;
      const result = await load(venue);
      if (cancelled) return;

      if (result?.error) {
        setState({ status: 'error', venue, error: result.error });
        return;
      }

      setState({ status: 'ready', venue, data: result?.data });
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueSlug, nonce, ...deps]);

  return { ...state, reload };
}
